import { apiFetch, readJsonResponse } from '../api/client';

export const ALL_RISK_SERIES = [
  { riskNumber: 1, key: 'Связь', color: '#0284c7' },
  { riskNumber: 2, key: 'Показания', color: '#d97706' },
  { riskNumber: 3, key: 'Сигнал', color: '#7c3aed' },
  { riskNumber: 4, key: 'Батарея', color: '#e11d48' },
];

function toPct(atRisk, total) {
  if (!total) {
    return 0;
  }
  return Math.round((Number(atRisk) / Number(total)) * 10000) / 100;
}

/** Aggregate every firmware version into one % series for a risk. */
function aggregateRiskPayload(payload, label) {
  const bySlot = new Map();
  for (const series of payload?.series || []) {
    for (const point of series.points || []) {
      const slot = point.snapshot_at || point.snapshot_date;
      if (!slot) {
        continue;
      }
      let bucket = bySlot.get(slot);
      if (!bucket) {
        bucket = {
          snapshot_at: slot,
          snapshot_date: point.snapshot_date || String(slot).slice(0, 10),
          total: 0,
          at_risk: 0,
        };
        bySlot.set(slot, bucket);
      }
      bucket.total += Number(point.total) || 0;
      bucket.at_risk += Number(point.at_risk) || 0;
    }
  }

  const points = [...bySlot.values()]
    .sort((left, right) => String(left.snapshot_at).localeCompare(String(right.snapshot_at)))
    .map((bucket) => ({
      snapshot_date: bucket.snapshot_date,
      snapshot_at: bucket.snapshot_at,
      total: bucket.total,
      at_risk: bucket.at_risk,
      at_risk_pct: toPct(bucket.at_risk, bucket.total),
    }));

  return {
    firmware_version: label,
    points,
  };
}

async function fetchRiskHistory(organizationId, connectionType, historyRange, hideTest, riskNumber) {
  const query = new URLSearchParams({
    organization_id: String(organizationId),
    connection_type: connectionType,
    range: historyRange || 'month',
    risk_number: String(riskNumber),
    hide_test: hideTest === false ? '0' : '1',
  });
  const response = await apiFetch(`/api/firmware/stats/history?${query}`);
  const payload = await readJsonResponse(response, 'Firmware history');
  if (!response.ok) {
    return null;
  }
  return payload;
}

/**
 * One chart payload with four risk lines (fleet % for the connection type).
 * Also keeps per-risk version series for table sparklines.
 */
export async function loadAllRisksHistoryPayload({
  organizationId,
  connectionType,
  historyRange = 'month',
  hideTest = true,
}) {
  if (!organizationId || !connectionType) {
    return null;
  }

  const payloads = await Promise.all(
    ALL_RISK_SERIES.map((item) =>
      fetchRiskHistory(organizationId, connectionType, historyRange, hideTest, item.riskNumber),
    ),
  );

  const byRisk = {};
  const series = [];
  const dateSet = new Set();

  for (let index = 0; index < ALL_RISK_SERIES.length; index += 1) {
    const meta = ALL_RISK_SERIES[index];
    const payload = payloads[index];
    byRisk[meta.riskNumber] = payload;
    if (!payload) {
      continue;
    }
    for (const slot of payload.dates || []) {
      dateSet.add(slot);
    }
    series.push(aggregateRiskPayload(payload, meta.key));
  }

  const dates = [...dateSet].sort((left, right) => String(left).localeCompare(String(right)));

  return {
    organization_id: Number(organizationId),
    risk_number: 'all',
    connection_type: connectionType,
    range: historyRange,
    hide_test: hideTest !== false,
    dates,
    slots: dates,
    series,
    by_risk: byRisk,
    series_colors: Object.fromEntries(ALL_RISK_SERIES.map((item) => [item.key, item.color])),
  };
}

/** Replace Telegram 7-day sparks with history for the selected chart period. */
export function attachAllRisksSeriesToReport(report, wifiHistory, nbiotHistory) {
  if (!report) {
    return report;
  }

  function enrichGroup(group, history) {
    if (!group) {
      return group;
    }
    const dates = history?.dates || [];
    const byRisk = history?.by_risk || {};
    const rows = (group.rows || []).map((row) => {
      const series = [1, 2, 3, 4].map((riskNumber) => {
        const payload = byRisk[riskNumber];
        const versionSeries = (payload?.series || []).find(
          (item) => String(item.firmware_version) === String(row.version),
        );
        const bySlot = new Map(
          (versionSeries?.points || []).map((point) => [
            point.snapshot_at || point.snapshot_date,
            point.at_risk_pct,
          ]),
        );
        if (!dates.length) {
          return row.series?.[riskNumber - 1] || [];
        }
        return dates.map((slot) => {
          const value = bySlot.get(slot);
          return value == null || !Number.isFinite(Number(value)) ? null : Number(value);
        });
      });
      return { ...row, series };
    });
    return { ...group, rows };
  }

  return {
    ...report,
    wifi: enrichGroup(report.wifi, wifiHistory),
    nbiot: enrichGroup(report.nbiot, nbiotHistory),
  };
}
